// src/components/Chat/shared/FilesAndLinksModal.tsx
// Modal para listar arquivos e links da sala de chat

import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  FileText,
  Image as ImageIcon,
  File,
  Link2,
  Download,
  Edit2,
  Check,
  ExternalLink,
  Folder,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useRef } from "react";
import ChatService from "@/services/ChatService";
import type { RoomFile, RoomLink } from "@/types/chat";
import { formatFileSize } from "@/types/chat";

type TabType = "files" | "links";
type FilterType = "all" | "images" | "documents" | "others";

interface FilesAndLinksModalProps {
  isOpen: boolean;
  roomId: string | number;
  currentUserId: number;
  isAdmin?: boolean;
  onClose: () => void;
}

export function FilesAndLinksModal({
  isOpen,
  roomId,
  currentUserId,
  isAdmin = false,
  onClose,
}: FilesAndLinksModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>("files");
  const [filter, setFilter] = useState<FilterType>("all");
  const [files, setFiles] = useState<RoomFile[]>([]);
  const [links, setLinks] = useState<RoomLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [hasMoreLinks, setHasMoreLinks] = useState(true);
  const [filePage, setFilePage] = useState(1);
  const [linkPage, setLinkPage] = useState(1);
  const [editingFile, setEditingFile] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Carregar arquivos
  const loadFiles = useCallback(
    async (page = 1, reset = false) => {
      if (!roomId) return;
      setIsLoading(true);

      try {
        const response = await ChatService.listarArquivos(
          roomId,
          page,
          20,
          filter,
        );

        // API retorna: { files: [...], summary: {...} }
        // Ou com paginação: { data: { files: [...] }, meta: {...} }
        const responseData = response?.data;
        const newFiles = responseData?.files || responseData?.data?.files || [];
        const meta = responseData?.meta || responseData?.pagination;

        if (newFiles.length > 0 || reset) {
          setFiles((prev) => (reset ? newFiles : [...prev, ...newFiles]));
          setHasMoreFiles(
            meta ? meta.currentPage < meta.lastPage : newFiles.length === 20,
          );
          setFilePage(page);
        }
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [roomId, filter],
  );

  // Carregar links
  const loadLinks = useCallback(
    async (page = 1, reset = false) => {
      if (!roomId) return;
      setIsLoading(true);

      try {
        const response = await ChatService.listarLinks(roomId, page, 20);

        // API retorna: { success: true, data: { links: [...] }, meta: {...} }
        const responseData = response?.data;
        const linksData =
          responseData?.data?.links || responseData?.links || [];
        const meta = responseData?.meta;

        if (linksData.length > 0 || reset) {
          setLinks((prev) => (reset ? linksData : [...prev, ...linksData]));
          setHasMoreLinks(
            meta ? meta.currentPage < meta.lastPage : linksData.length === 20,
          );
          setLinkPage(page);
        }
      } catch (error) {
        console.error("Erro ao carregar links:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [roomId],
  );

  // Carregar dados ao abrir
  useEffect(() => {
    if (isOpen && roomId) {
      if (activeTab === "files") {
        loadFiles(1, true);
      } else {
        loadLinks(1, true);
      }
    }
  }, [isOpen, roomId, activeTab, loadFiles, loadLinks]);

  // Recarregar ao mudar filtro
  useEffect(() => {
    if (isOpen && activeTab === "files") {
      loadFiles(1, true);
    }
  }, [filter, isOpen, activeTab, loadFiles]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      if (activeTab === "files" && hasMoreFiles) {
        loadFiles(filePage + 1);
      } else if (activeTab === "links" && hasMoreLinks) {
        loadLinks(linkPage + 1);
      }
    }
  }, [
    activeTab,
    hasMoreFiles,
    hasMoreLinks,
    filePage,
    linkPage,
    isLoading,
    loadFiles,
    loadLinks,
  ]);

  // Renomear arquivo
  const handleRename = async (file: RoomFile) => {
    if (!editName.trim() || editName === file.displayName) {
      setEditingFile(null);
      return;
    }

    try {
      const response = await ChatService.renomearArquivo(
        file.messageId,
        file.fileIndex,
        editName,
      );

      if (response?.data?.success) {
        setFiles((prev) =>
          prev.map((f) =>
            f.messageId === file.messageId && f.fileIndex === file.fileIndex
              ? { ...f, displayName: editName }
              : f,
          ),
        );
      }
    } catch (error) {
      console.error("Erro ao renomear:", error);
    } finally {
      setEditingFile(null);
    }
  };

  // Ícone do arquivo
  const getFileIcon = (category: RoomFile["category"]) => {
    switch (category) {
      case "images":
        return <ImageIcon size={24} className="text-green-500" />;
      case "documents":
        return <FileText size={24} className="text-blue-500" />;
      default:
        return <File size={24} className="text-gray-500" />;
    }
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Verificar permissão de edição
  const canEdit = (file: RoomFile) => {
    return isAdmin || file.sentBy.id === currentUserId;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-800">
                <Folder size={20} className="text-blue-500" />
                <h3 className="font-semibold">
                  {t("chatFeatures.files.title")}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("files")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === "files"
                    ? "text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <FileText size={16} />
                  {t("chatFeatures.files.tabFiles")}
                </span>
                {activeTab === "files" && (
                  <motion.div
                    layoutId="tabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  />
                )}
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("links")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === "links"
                    ? "text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <Link2 size={16} />
                  {t("chatFeatures.files.tabLinks")}
                </span>
                {activeTab === "links" && (
                  <motion.div
                    layoutId="tabIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  />
                )}
              </motion.button>
            </div>

            {/* Filtros (só para arquivos) */}
            {activeTab === "files" && (
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 p-3 border-b border-gray-100">
                {(["all", "images", "documents", "others"] as FilterType[]).map(
                  (f) => (
                    <motion.button
                      key={f}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                        filter === f
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {t(
                        `chatFeatures.files.filter${f.charAt(0).toUpperCase() + f.slice(1)}`,
                      )}
                    </motion.button>
                  ),
                )}
              </div>
            )}

            {/* Conteúdo */}
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4"
            >
              {/* Tab Arquivos */}
              {activeTab === "files" && (
                <div className="space-y-2">
                  {files.length === 0 && !isLoading ? (
                    <div className="text-center py-12">
                      <FileText
                        size={48}
                        className="mx-auto text-gray-300 mb-3"
                      />
                      <p className="text-gray-500">
                        {t("chatFeatures.files.noFiles")}
                      </p>
                    </div>
                  ) : (
                    files.map((file, index) => (
                      <motion.div
                        key={`${file.messageId}-${file.fileIndex}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                      >
                        {/* Ícone */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          {getFileIcon(file.category)}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          {editingFile === file.messageId ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleRename(file);
                                  if (e.key === "Escape") setEditingFile(null);
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={() => handleRename(file)}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                              >
                                <Check size={16} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {file.displayName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)} •{" "}
                                {t("chatFeatures.files.sentBy")}{" "}
                                {file.sentBy.name}{" "}
                                {t("chatFeatures.files.sentAt")}{" "}
                                {formatDate(file.sentAt)}
                              </p>
                            </>
                          )}
                        </div>

                        {/* Ações */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEdit(file) && editingFile !== file.messageId && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditingFile(file.messageId);
                                setEditName(file.displayName);
                              }}
                              className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500"
                              title={t("chatFeatures.files.rename")}
                            >
                              <Edit2 size={14} />
                            </motion.button>
                          )}
                          <motion.a
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            href={file.url}
                            download={file.displayName}
                            className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500"
                            title={t("chatFeatures.files.download")}
                          >
                            <Download size={14} />
                          </motion.a>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Tab Links */}
              {activeTab === "links" && (
                <div className="space-y-2">
                  {links.length === 0 && !isLoading ? (
                    <div className="text-center py-12">
                      <Link2 size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        {t("chatFeatures.files.noLinks")}
                      </p>
                    </div>
                  ) : (
                    links.map((link, index) => (
                      <motion.a
                        key={`${link.messageId}-${index}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                      >
                        {/* Ícone */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Link2 size={20} className="text-blue-600" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-blue-600 truncate group-hover:underline">
                            {link.domain}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {link.messagePreview}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t("chatFeatures.files.sentBy")} {link.sentBy.name}{" "}
                            {t("chatFeatures.files.sentAt")}{" "}
                            {formatDate(link.sentAt)}
                          </p>
                        </div>

                        {/* Ícone externo */}
                        <ExternalLink
                          size={14}
                          className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </motion.a>
                    ))
                  )}
                </div>
              )}

              {/* Loading */}
              {isLoading && (
                <div className="flex justify-center py-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full"
                  />
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default FilesAndLinksModal;
